import { dropRightWhile, uniq } from 'lodash';
import { isTruthy, openUrlInNewTab } from '@ardoq/common-helpers';
import {
  isComponent,
  isReference,
  isWorkspace,
  ViewIds,
  type APIComponentAttributes,
  type ArdoqId,
} from '@ardoq/api-types';
import { componentInterface } from '@ardoq/component-interface';
import { referenceInterface } from '@ardoq/reference-interface';
import { workspaceInterface } from '@ardoq/workspace-interface';
import {
  DropdownOptionType,
  isDropdownDivider,
  type DropdownButtonOption,
  type DropdownItem,
} from '@ardoq/dropdown-menu';
import { IconName } from '@ardoq/icons';
import { t, type I18nLocale } from '@ardoq/i18n';
import type { UrlFieldValue, Vector } from '@ardoq/graph';
import { getDropdownDivider } from 'contextMenus/menuItems';
import { getCommonMenuData } from 'contextMenus/getCommonMenuData';
import { type WorkspaceMenuState } from 'contextMenus/workspaceMenu';
import {
  type GetScenarioContextMenuOptions,
  getScenarioContextMenuOptionsForView,
} from 'scope/scenarioMenuOptions';
import type { PermissionContext } from '@ardoq/access-control';
import type {
  CurrentContext,
  WorkspaceAndModelLookup,
} from '@ardoq/data-model';
import type { FeatureContext } from '@ardoq/features';
import { dispatchAction } from '@ardoq/rxbeach';
import { buildReferenceComponents } from 'contextMenus/referenceComponentsInterface';
import { trackUrlLinkClicked } from 'tabview/graphViews/tracking';
import type {
  AqBlocks,
  ItemSet,
  OnContextMenu,
} from 'tabview/visualizations/aq-blocks/types';
import {
  blocksContextMenuRequested,
  type BlocksContextMenuRequest,
} from './actions';
import { type ReferenceCreationMenuState } from 'streams/referenceCreationMenuState$';

/** The aq-blocks view a context menu opens in. */
export type ContextMenuView = { viewId: ViewIds; i18nLocale: I18nLocale };

export type BlocksContextMenuAppState = {
  permissionContext: PermissionContext;
  featureContext: FeatureContext;
  currentContext: CurrentContext;
  hasCollapsingRuleApplied: boolean;
  workspaceMenuState: WorkspaceMenuState;
  workspaceAndModel: WorkspaceAndModelLookup;
  componentsById: Record<ArdoqId, APIComponentAttributes>;
  referenceCreation: ReferenceCreationMenuState;
};

const urlOption =
  (viewId: ViewIds) =>
  ({ label, url }: UrlFieldValue, index: number): DropdownButtonOption => ({
    type: DropdownOptionType.BUTTON_OPTION,
    name: `open-url-${index}`,
    label,
    iconName: IconName.OPEN_IN_NEW,
    onClick: () => {
      openUrlInNewTab(url);
      trackUrlLinkClicked(viewId);
    },
  });

// One URL field → a single entry; multiple → a submenu (ARD-33060).
const urlOptions = (
  urlFieldValues: UrlFieldValue[] | null,
  { viewId, i18nLocale }: ContextMenuView
): DropdownItem[] => {
  const urls = (urlFieldValues ?? []).filter(
    ({ url }) => url.trim().length > 0
  );
  if (urls.length === 0) return [];
  if (urls.length === 1) return [urlOption(viewId)(urls[0], 0)];
  return [
    {
      type: DropdownOptionType.SUBMENU,
      name: 'open-url',
      label: t(
        i18nLocale,
        'flexibleBlockDiagram:contextMenu.openUrl',
        'Open URL'
      ),
      iconName: IconName.OPEN_IN_NEW,
      options: urls.map(urlOption(viewId)),
    },
  ];
};

/**
 * Context-menu options for one item out of a set: a hovered node's URL fields; empty for a
 * reference or when there is nothing actionable. `createContextMenuHandler` passes what the
 * pointer is on when `isPointerOnSelection`, alongside the shared menus for the whole
 * selection — and the selection alone, with no shared menus, when it is not.
 */
export const buildContextMenuOptions = (
  items: ItemSet,
  view: ContextMenuView
): DropdownItem[] => {
  const node = items.node();
  if (items.link() || !node) return [];

  return urlOptions(node.urlFieldValues, view);
};

export type ContextMenuModelIds = {
  nodeModelIds: ArdoqId[];
  referenceModelIds: ArdoqId[];
};

/**
 * The model ids a diagram selection stands for. Nodes are kept whatever they
 * turn out to be. Grouping can emit several nodes for one `modelId`, which a
 * menu acting on the selection must not see twice.
 */
export const toContextMenuModelIds = (items: ItemSet): ContextMenuModelIds => ({
  nodeModelIds: uniq(
    [...items.nodes()].map(node => node.modelId).filter(isTruthy)
  ),
  referenceModelIds: uniq(
    [...items.links()].map(reference => reference.modelId).filter(isTruthy)
  ),
});

/**
 * The id the component menu treats as the one right-clicked, withheld unless it
 * is among the components resolved: the menu rejects and reports any other.
 */
export const toEventTargetModelId = (
  hoveredModelId: ArdoqId | null,
  componentIds: ArdoqId[]
): ArdoqId | undefined => {
  if (!hoveredModelId) return undefined;
  if (componentIds.length === 0) return hoveredModelId;
  return componentIds.includes(hoveredModelId) ? hoveredModelId : undefined;
};

/** The standard component, reference, mixed and workspace menus. */
const sharedEntityOptions = (
  component: AqBlocks,
  items: ItemSet,
  client: Vector,
  i18nLocale: I18nLocale,
  permissionContext: PermissionContext,
  workspaceMenuState: WorkspaceMenuState,
  workspaceAndModel: WorkspaceAndModelLookup,
  componentsById: Record<ArdoqId, APIComponentAttributes>,
  referenceCreation: ReferenceCreationMenuState,
  getScenarioContextMenuOptions: GetScenarioContextMenuOptions
): DropdownItem[] => {
  const { nodeModelIds, referenceModelIds } = toContextMenuModelIds(items);
  const references = referenceModelIds
    .map(referenceInterface.get)
    .filter(isReference);
  const components = nodeModelIds
    .map(componentInterface.get)
    .filter(isComponent);

  const { options } = getCommonMenuData(
    {
      target: component.canvas,
      event: null,
      x: client[0],
      y: client[1],
      isViewpointMode: true,
    },
    {
      components,
      referenceComponents: buildReferenceComponents(references),
      workspaces: nodeModelIds.map(workspaceInterface.get).filter(isWorkspace),
      workspaceMenuState,
      workspaceAndModel,
      componentsById,
      referenceCreation,
      eventTargetModelId: toEventTargetModelId(
        component.hovered.node()?.modelId ?? null,
        components.map(({ _id }) => _id)
      ),
    },
    i18nLocale,
    permissionContext,
    getScenarioContextMenuOptions
  );
  return options ?? [];
};

const withoutAdjacentDividers = (options: DropdownItem[]): DropdownItem[] =>
  options.filter(
    (option, index) =>
      !(
        isDropdownDivider(option) &&
        index > 0 &&
        isDropdownDivider(options[index - 1])
      )
  );

export const withDivider = (
  sharedOptions: DropdownItem[],
  viewOptions: DropdownItem[]
): DropdownItem[] => {
  const shared = dropRightWhile(
    withoutAdjacentDividers(sharedOptions),
    isDropdownDivider
  );
  if (shared.length === 0) return viewOptions;
  if (viewOptions.length === 0) return shared;
  return [...shared, getDropdownDivider(), ...viewOptions];
};

/**
 * Whether what the pointer is on is part of the selection. A right-click puts what it hit
 * into the selection unless it is already there, so whatever it hit is inside — including
 * a member of a standing multi-selection rather than a fresh hit.
 */
export const isPointerOnSelection = (
  hovered: ItemSet,
  items: ItemSet
): boolean => hovered.size > 0 && hovered.isSubsetOf(items);

/**
 * The options for a context-menu request: the shared entity menus plus the view's own
 * when the pointer is on the selection, and the selection's own entries otherwise.
 */
export const getContextMenuOptions = (
  { component, items, client, view }: BlocksContextMenuRequest,
  {
    permissionContext,
    featureContext,
    currentContext,
    hasCollapsingRuleApplied,
    workspaceMenuState,
    workspaceAndModel,
    componentsById,
    referenceCreation,
  }: BlocksContextMenuAppState
): DropdownItem[] => {
  if (!isPointerOnSelection(component.hovered, items)) {
    return buildContextMenuOptions(items, view);
  }
  const { viewId, i18nLocale } = view;
  return withDivider(
    sharedEntityOptions(
      component,
      items,
      client,
      i18nLocale,
      permissionContext,
      workspaceMenuState,
      workspaceAndModel,
      componentsById,
      referenceCreation,
      getScenarioContextMenuOptionsForView({
        isViewpointMode: true,
        viewId,
        featureContext,
        permissionContext,
        context: currentContext,
        hasCollapsingRuleApplied,
        i18nLocale,
      })
    ),
    buildContextMenuOptions(component.hovered, view)
  );
};

/**
 * aq-blocks `doContextMenu` handler: request a context menu for what was
 * right-clicked, and close it on teardown. The global context-menu renderer
 * (GlobalContextMenus) handles positioning and dismissal.
 */
export const createContextMenuHandler =
  (view: ContextMenuView, closeContextMenu: VoidFunction): OnContextMenu =>
  (component, items, client) => {
    dispatchAction(
      blocksContextMenuRequested({ component, items, client, view })
    );
    return closeContextMenu;
  };
